import isUndefined from 'lodash.isundefined';
import { Observable, of, ReplaySubject, Subject, throwError } from 'rxjs';
import { filter, switchMap, map, tap, catchError, takeUntil } from 'rxjs/operators';
import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';

@Injectable()
export abstract class HttpCrudService implements OnDestroy {
  private isInitialized = false;
  private dataSubject$ = new ReplaySubject<any>(1);
  private unsubscribe$ = new Subject<void>();

  protected data$ = this.dataSubject$.pipe(
    filter((data) => !isUndefined(data)),
  );

  constructor(
    protected http: HttpClient,
  ) {}

  public abstract read(): Observable<any>;

  public fetch(): Observable<any> {
    const fetch$ = new Subject<any>();
    const read$ = this.read().pipe(
      tap((data) => {
        this.dataSubject$.next(data);
        if (!this.isInitialized) {
          this.isInitialized = true;
        }
      }),
    );

    read$.pipe(takeUntil(this.unsubscribe$)).subscribe(fetch$);

    return fetch$.asObservable();
  }

  public clear(): void {
    this.dataSubject$.next();
  }

  protected request(requestConfig: { url, method?, data?, reload? }): Observable<any> {
    const { method = 'get', url, data } = requestConfig;

    return this.http[method](url, data).pipe(
      switchMap(response => this.handleSuccessResponse(response, requestConfig)),
      catchError(error => this.handleError(error)),
    );
  }

  private handleSuccessResponse(response, requestConfig): Observable<any> {
    if (requestConfig.reload) {
      return this.fetch().pipe(
        map(() => response),
      );
    }

    return of(response);
  }

  private handleError(error): Observable<any> {
    return throwError(error);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
